import React, { useEffect } from 'react';
import type { FC } from 'react';

import type { LoadableType } from '@confluence/loadable';
import { LoadableAfterPaint } from '@confluence/loadable';
import {
	manuallyLogExpExposure,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';

import { EndOfPageRecPlaceholderContainer } from './SharedComponents.tsx';
import type { EndOfPageRecommendationProps } from './EndOfPageRecommendation.tsx';

// One loadable per arm with an UNCONDITIONAL loader. The arm must be chosen at render
// time (below), never inside a loader: on the server loaders run once at module-init,
// before per-request Statsig is ready, so a gate read there caches the default (control)
// for the whole process and treatment never SSRs.
const EndOfPageRecommendationControlLoadable: LoadableType<EndOfPageRecommendationProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-EndOfPageRecommendation" */ './EndOfPageRecommendation.tsx'
				)
			).EndOfPageRecommendation,
	});

const EndOfPageRecommendationTwgLoadable: LoadableType<EndOfPageRecommendationProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-EndOfPageRecommendationTwg" */ './EndOfPageRecommendationTwg.tsx'
				)
			).EndOfPageRecommendationTwg,
	});

// Renders the cohort's arm, choosing it at render time (inline, no-exposure) so SSR picks
// the correct arm per request. Exposure is logged separately (see FinalLoader). `.preload`
// warms both arms for the editor loadables preloader.
// `Object.assign` + explicit type so `isolatedDeclarations` can emit the `.preload`
// property (assigning it by mutation isn't supported under isolated declarations).
export const EndOfPageRecommendationLoader: FC<EndOfPageRecommendationProps> & {
	preload: () => void;
} = Object.assign(
	(props: EndOfPageRecommendationProps): React.JSX.Element =>
		UNSAFE_expValEqualsNoExposure('confluence_eop_recs_twg_source_exp', 'isEnabled', true) ? (
			<EndOfPageRecommendationTwgLoadable {...props} />
		) : (
			<EndOfPageRecommendationControlLoadable {...props} />
		),
	{
		preload: () => {
			EndOfPageRecommendationControlLoadable.preload();
			EndOfPageRecommendationTwgLoadable.preload();
		},
	},
);

export const EndOfPageRecommendationFinalLoader: FC<EndOfPageRecommendationProps> = (props) => {
	// Single exposure point: client-side on mount, before the scroll/loadable boundary, so
	// it covers the whole page/blog population (not just users who scroll to the recs).
	useEffect(() => {
		manuallyLogExpExposure('confluence_eop_recs_twg_source_exp');
	}, []);

	// LoadableAfterPaint (not a SCROLL_INTO_VIEW LoadableActivatable): the scroll trigger
	// fires setIsInViewport() synchronously during commit/hydration, causing React error
	// #421 inside a SuspenseWithDataFetcher boundary while scheduleSyncHydration is active.
	return (
		<SuspenseWithDataFetcher
			dataFetcherName="end-of-page-recommendations"
			fallback={<EndOfPageRecPlaceholderContainer />}
		>
			<EndOfPageRecommendationLoader {...props} />
		</SuspenseWithDataFetcher>
	);
};
